import React, { useEffect, useState } from "react";
import { FaTimes, FaUserFriends, FaUserShield, FaLayerGroup, FaEye, FaFilter } from "react-icons/fa";

// Reusing colors
const COLORS = {
  DIVIDER: "#E6EEF2",
  BORDER: "rgba(226,232,240,1)",
  BABY1: "#AAD4F1",
};

const getAuthHeader = () => {
  const token = localStorage.getItem('authToken');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

// Configuration for Filter Screens (Same as Form Modal)
const FILTER_CONFIG_MAP = {
  'MRN_MASTER': [
    { key: 'WAREHOUSE', label: 'Location', endpoint: '/locations', idField: 'LocationCode', labelField: 'LocationName' },
    { key: 'Branch', label: 'Branch', endpoint: '/branches', idField: 'BranchCode', labelField: 'BranchName' }
  ],
  'DEFAULT': [
    { key: 'ProjectList', label: 'Project', endpoint: '/project', idField: 'No', labelField: 'Description' },
    { key: 'Feeder', label: 'Feeder', endpoint: '/feeder', idField: 'Code', labelField: 'Description' }
  ]
};

const ApprovalViewModal = ({ open, onClose, apiBaseUrl, viewingFlowId }) => {
  const [flowData, setFlowData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [profiles, setProfiles] = useState([]);
  
  // State for Filter Options (to resolve IDs to Names)
  const [filterOptions, setFilterOptions] = useState({});

  useEffect(() => {
    if (open && viewingFlowId) {
      fetchFlowData();
      fetchDropdownData();
    }
  }, [open, viewingFlowId]);

  // Fetch flow data and then fetch necessary filter details
  const fetchFlowData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBaseUrl}/approval-flows/${viewingFlowId}`, { headers: getAuthHeader() });
      const data = await res.json();
      setFlowData(data);
      
      // If filters exist, fetch their details for display
      if (data.filters && data.filters.length > 0) {
        fetchFilterDetails(data.filters);
      }
    } catch (err) {
      console.error("Failed to load flow", err);
      alert("Could not load flow");
    } finally {
      setLoading(false);
    }
  };

  const fetchDropdownData = async () => {
    try {
      const [uRes, pRes] = await Promise.all([
        fetch(`${apiBaseUrl}/user`, { headers: getAuthHeader() }),
        fetch(`${apiBaseUrl}/profiles-name`, { headers: getAuthHeader() })
      ]);
      const uData = await uRes.json();
      const pData = await pRes.json();
      setUsers(Array.isArray(uData) ? uData : []);
      setProfiles(Array.isArray(pData) ? pData : []);
    } catch (err) {
      console.error("Error fetching dropdowns", err);
    }
  };

  // Helper to fetch master data for filter display names
  const fetchFilterDetails = async (filters) => {
    const typesToFetch = new Set(filters.map(f => f.FilterMaster));
    
    for (const type of typesToFetch) {
      // Find config for this type
      const config = Object.values(FILTER_CONFIG_MAP).flat().find(c => c.key === type);
      if (config && !filterOptions[type]) {
        try {
            // We can use the generic endpoint or the available-values endpoint
            // Using generic endpoint to get all details
            const res = await fetch(`${apiBaseUrl}${config.endpoint}`, { headers: getAuthHeader() });
            const data = await res.json();
            const list = Array.isArray(data) ? data : (data.screens || data.data || []);
            setFilterOptions(prev => ({ ...prev, [type]: list }));
        } catch (err) {
            console.error(`Error fetching details for ${type}`, err);
        }
      }
    }
  };

  if (!open) return null;

  // Get available filter types for this document type
  const availableFilterTypes = FILTER_CONFIG_MAP[flowData?.DocumentType] || FILTER_CONFIG_MAP['DEFAULT'];

  return (
    <div className="fixed inset-0 z-50 flex bg-black bg-opacity-50 transition-opacity duration-300">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
      
      <div className="relative z-10 bg-white shadow-2xl w-full flex flex-col transition-all duration-300 ease-in-out m-auto h-full max-w-6xl">
        
        {/* HEADER */}
        <div className="flex-shrink-0 p-5 border-b flex justify-between items-center" style={{ borderColor: COLORS.BORDER, backgroundColor: "#F8FAFC" }}>
          <div>
            <div className="flex items-center gap-2">
                <h3 className="text-xl font-bold text-slate-900">View Approval Workflow</h3>
                <span className="bg-gray-200 text-gray-600 text-xs font-bold px-2 py-0.5 rounded uppercase">Read Only</span>
            </div>
            <p className="text-sm text-gray-500">Viewing configuration details</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-200 transition-colors">
            <FaTimes className="w-6 h-6" />
          </button>
        </div>

        {/* CONTENT */}
        <div className="flex-1 p-6 overflow-y-auto custom-scrollbar bg-gray-50">
          
          {/* Top Info Section */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="col-span-2">
              <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Workflow Name</label>
              <div className="w-full p-3 rounded border bg-white text-gray-900 font-semibold" style={{ borderColor: COLORS.BORDER }}>
                {flowData?.FlowName || '-'}
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Document Type</label>
              <div className="w-full p-3 rounded border bg-white text-gray-900 font-semibold uppercase" style={{ borderColor: COLORS.BORDER }}>
                {flowData?.DocumentType || '-'}
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Document Type</label>
              <div className="w-full p-3 rounded border bg-white text-gray-900 font-semibold uppercase" style={{ borderColor: COLORS.BORDER }}>
                {flowData?.IsActive ? 'Active' : 'Inactive' }
              </div>
            </div>
          </div>

          {/* ==========================================
              NEW: DATA SCOPE FILTERS SECTION
           ========================================== */}
          <div className="bg-white border rounded-lg shadow-sm mb-6 p-6" style={{ borderColor: COLORS.BORDER }}>
             <div className="flex items-center mb-4">
                <h4 className="text-md font-bold text-slate-800 flex items-center gap-2">
                    <FaFilter /> Data Scope Filters
                </h4>
             </div>

             {loading ? (
                 <div className="h-20 flex items-center justify-center text-gray-400">Loading filters...</div>
             ) : !flowData?.filters || flowData.filters.length === 0 ? (
                 <div className="text-center py-4 text-gray-400 border-dashed border rounded bg-gray-50">
                     No filters applied. This workflow applies to all {flowData?.DocumentType} entries.
                 </div>
             ) : (
                <div className="space-y-4">
                    {flowData.filters.map((filter, index) => {
                        const config = availableFilterTypes.find(t => t.key === filter.FilterMaster);
                        const options = filterOptions[filter.FilterMaster] || [];
                        
                        return (
                            <div key={index} className="flex gap-4 items-start bg-gray-50 p-4 border rounded-lg">
                                <div className="w-1/3">
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Filter Type</label>
                                    <div className="w-full p-2 bg-white border rounded text-gray-800 font-medium">
                                        {config?.label || filter.FilterMaster}
                                    </div>
                                </div>
                                
                                <div className="flex-1">
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Selected Values</label>
                                    <div className="w-full p-2 bg-white border rounded min-h-[42px]">
                                        <div className="flex flex-wrap gap-2">
                                            {filter.Values.map(val => {
                                                const item = options.find(o => o[config?.idField] === val);
                                                const displayText = item 
                                                    ? `${item[config?.idField]} - ${item[config?.labelField]}` 
                                                    : val;
                                                return (
                                                    <span key={val} className="bg-blue-100 text-blue-800 px-3 py-1 rounded text-xs font-medium shadow-sm">
                                                        {displayText}
                                                    </span>
                                                )
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>
             )}
          </div>

          {/* Approval Sequence */}
          <div className="space-y-6">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-md font-bold text-slate-800 flex items-center gap-2">
                <FaLayerGroup /> Approval Sequence
              </h4>
            </div>

            {loading ? (
               <div className="flex justify-center py-10"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>
            ) : !flowData || flowData.levels.length === 0 ? (
              <div className="text-center py-12 bg-white border-2 border-dashed rounded-lg border-gray-300">
                <p className="text-gray-500 font-medium">No levels configured.</p>
              </div>
            ) : (
              flowData.levels.map((level, idx) => (
                <div key={idx} className="bg-white border rounded-lg shadow-sm overflow-hidden" style={{ borderColor: COLORS.BORDER }}>
                  
                  {/* Level Header */}
                  <div className="flex justify-between items-center p-4 bg-gray-50 border-b" style={{ borderColor: COLORS.DIVIDER }}>
                    <div className="flex items-center gap-3">
                      <span className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-700 font-bold text-sm">
                        {level.LevelNumber}
                      </span>
                      <span className="font-bold text-gray-700">Level {level.LevelNumber}</span>
                      
                      {level.Approvers.length > 0 && (
                          <span className="ml-2 text-xs font-bold text-green-600 bg-green-100 px-2 py-0.5 rounded-full border border-green-200">
                              Configured ({level.Approvers.length}/3)
                          </span>
                      )}
                    </div>
                  </div>

                  {/* Level Body */}
                  <div className="border-t p-6" style={{ borderColor: COLORS.DIVIDER }}>
                    
                    {/* Type Display */}
                    <div className="mb-6 flex items-center">
                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mr-4">Approval Based On:</label>
                        <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-bold transition-all ${
                            level.ApprovalType === 'USER_WISE' ? 'bg-blue-50 text-blue-600' : 'bg-purple-50 text-purple-600'
                        }`}>
                            {level.ApprovalType === 'USER_WISE' ? <FaUserFriends /> : <FaUserShield />}
                            {level.ApprovalType === 'USER_WISE' ? 'Specific Users' : 'Roles'}
                        </div>
                    </div>

                    {/* Selected Approvers Display */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                        {level.Approvers.length > 0 ? (
                            level.Approvers.map(approverId => {
                                const user = level.ApprovalType === 'USER_WISE' 
                                  ? users.find(u => u.UserID === approverId) 
                                  : profiles.find(p => p.ProfileID === approverId);
                                
                                if (!user) return null;

                                const isUser = level.ApprovalType === 'USER_WISE';
                                
                                return (
                                    <div 
                                      key={approverId} 
                                      className={`border rounded-lg p-4 flex items-center gap-3 shadow-sm relative ${
                                          isUser ? 'border-blue-100 bg-blue-50' : 'border-purple-100 bg-purple-50'
                                      }`}
                                    >
                                        <div className={`w-12 h-12 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${
                                            isUser ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                                        }`}>
                                            {user.FullName ? user.FullName.charAt(0) : <FaUserShield />}
                                        </div>
                                        
                                        <div className="overflow-hidden">
                                            <div className="text-sm font-bold text-gray-800 truncate">
                                                {user.FullName || user.ProfileName}
                                            </div>
                                            <div className="text-xs text-gray-500 truncate">
                                                {isUser ? user.Username : 'Role ID: ' + user.ProfileID}
                                            </div>
                                        </div>

                                        <div className={`ml-auto rounded-full p-1 ${
                                            isUser ? 'bg-blue-200 text-blue-800' : 'bg-purple-200 text-purple-800'
                                        }`}>
                                            <FaEye className="w-3 h-3" />
                                        </div>
                                    </div>
                                )
                            })
                        ) : (
                            <div className="col-span-full text-center py-6 bg-gray-50 rounded-lg border border-dashed text-gray-400 italic">
                                No approvers configured for this level.
                            </div>
                        )}
                    </div>
                  </div>

                </div>
              ))
            )}
          </div>
        </div>

        {/* FOOTER */}
        <div className="flex-shrink-0 p-5 border-t bg-gray-50 flex justify-end" style={{ borderColor: COLORS.BORDER }}>
          <button onClick={onClose} className="px-6 py-2.5 text-gray-600 font-bold hover:bg-gray-200 rounded transition-all">
            Close
          </button>
        </div>

      </div>
    </div>
  );
};

export default ApprovalViewModal;